import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

interface PlanInvite {
  id: string;
  email: string;
  plan: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
}

const PLANS = ['micro', 'plus', 'pro', 'studio', 'max'];

const STATUS_STYLE: Record<PlanInvite['status'], string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  revoked: 'bg-gray-100 text-gray-600',
  expired: 'bg-gray-100 text-gray-600',
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

/** Admin: invite writers to a complimentary plan by email. */
const AdminInvites: React.FC = () => {
  const navigate = useNavigate();
  const [invites, setInvites] = useState<PlanInvite[]>([]);
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState('plus');
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState<{ email: string; link: string; sent: boolean } | null>(
    null,
  );

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/admin/plan-invites', { headers: authHeaders() });
      setInvites(res.data);
    } catch (err: any) {
      if (err.response?.status === 403) navigate('/login');
      else toast.error('Failed to load invites');
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(
        '/admin/plan-invites',
        { email, plan },
        { headers: authHeaders() },
      );
      setLastLink({ email: res.data.invite.email, link: res.data.link, sent: res.data.emailSent });
      if (res.data.emailSent) toast.success(`Invite sent to ${res.data.invite.email}`);
      else toast.error('Invite created, but the email failed. Share the link below.');
      setEmail('');
      load();
    } catch (err: any) {
      const message = err.response?.data?.message;
      toast.error(Array.isArray(message) ? message[0] : message || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const revoke = async (invite: PlanInvite) => {
    if (!window.confirm(`Revoke the invite to ${invite.email}?`)) return;
    try {
      await axios.delete(`/admin/plan-invites/${invite.id}`, { headers: authHeaders() });
      toast.success('Invite revoked');
      load();
    } catch {
      toast.error('Failed to revoke invite');
    }
  };

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy; select the link instead');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h1 className="text-lg font-medium text-gray-900 mb-1">Invite a writer</h1>
          <p className="text-sm text-gray-500 mb-4">
            They get the plan free, with its words refilled every month, until you end it from their
            user page. The link works for that email address only and expires in 30 days.
          </p>
          <form onSubmit={send} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="writer@example.com"
              className="flex-1 min-w-0 border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm capitalize"
            >
              {PLANS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={sending}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send invite'}
            </button>
          </form>
          {lastLink && (
            <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm">
              <div className="text-gray-700 mb-1">
                {lastLink.sent ? 'Emailed' : 'Not emailed'} to {lastLink.email}. Link:
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 break-all text-xs text-gray-900">
                  {lastLink.link}
                </code>
                <button
                  type="button"
                  onClick={() => copy(lastLink.link)}
                  className="shrink-0 text-gray-500 hover:text-gray-900"
                  aria-label="Copy link"
                >
                  <ClipboardDocumentIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Invites</h2>
          {invites.length === 0 ? (
            <p className="text-sm text-gray-500">No invites yet</p>
          ) : (
            <div className="divide-y divide-gray-200">
              {invites.map((invite) => (
                <div key={invite.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 break-all">
                      {invite.email}
                    </div>
                    <div className="text-xs text-gray-500">
                      <span className="capitalize">{invite.plan}</span> · sent{' '}
                      {new Date(invite.createdAt).toLocaleDateString()}
                      {invite.acceptedAt &&
                        ` · joined ${new Date(invite.acceptedAt).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[invite.status]}`}
                    >
                      {invite.status}
                    </span>
                    {invite.status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => revoke(invite)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInvites;
